/**
 * GroupRow — All Groups list item: avatar, name, member count and the join CTA
 * whose copy comes from the shared join-action resolver.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_STATUS } from '@wlb/shared/constants';
import { getGroupJoinActionLabelKey } from '@wlb/shared/utils/groupJoinAction';
import type { Group } from '@wlb/shared/types/groups';
import { Button } from '../../components/ui/Button';
import { GroupsTabIcon } from '../../components/ui/icons';
import { GroupImage } from './GroupImage';
import { resolveGroupImageUrl } from './groupMedia';
import './groups.css';

interface GroupRowProps {
  group: Group;
  onSelect: (group: Group) => void;
  onJoin: (groupId: string) => void;
}

export function GroupRow({ group, onSelect, onJoin }: GroupRowProps) {
  const { t } = useTranslation();
  const imageUrl = resolveGroupImageUrl(group.groupImage);
  const isPending = group.status === GROUP_STATUS.PENDING;
  const isOwner = group.status === GROUP_STATUS.OWNER;
  const isJoined = group.status === GROUP_STATUS.JOINED || isOwner;
  const label = t(getGroupJoinActionLabelKey(group, 'home.allGroups'));

  return (
    <div className="group-row">
      <button type="button" className="group-row__main" onClick={() => onSelect(group)}>
        <GroupImage
          src={imageUrl}
          imageClassName="group-row__image"
          fallbackClassName="group-row__placeholder"
          fallback={<GroupsTabIcon size={24} />}
        />
        <span className="group-row__meta">
          <span className="group-row__name">{group.groupName}</span>
          <span className="group-row__members">
            {t('home.allGroups.members', { count: group.totalMembers.toLocaleString() })}
          </span>
        </span>
      </button>
      {isJoined || isPending ? (
        <span className="group-row__status">{label}</span>
      ) : (
        <Button size="sm" onClick={() => onJoin(group.id)}>{label}</Button>
      )}
    </div>
  );
}
